import type { Breakpoint } from '@mui/material/styles';

import { merge } from 'es-toolkit';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';

import { CONFIG } from 'src/global-config';

import { Logo } from 'src/components/logo';

import { AuthSplitSection } from './section';
import { AuthSplitContent } from './content';
import { MainSection } from '../core/main-section';
import { LayoutSection } from '../core/layout-section';
import { HeaderSection } from '../core/header-section';

import type { AuthSplitSectionProps } from './section';
import type { AuthSplitContentProps } from './content';
import type { MainSectionProps } from '../core/main-section';
import type { HeaderSectionProps } from '../core/header-section';
import type { LayoutSectionProps } from '../core/layout-section';

// ----------------------------------------------------------------------

type LayoutBaseProps = Pick<LayoutSectionProps, 'sx' | 'children' | 'cssVars'>;

export type AuthSplitLayoutProps = LayoutBaseProps & {
  layoutQuery?: Breakpoint;
  slotProps?: {
    header?: HeaderSectionProps;
    main?: MainSectionProps;
    section?: AuthSplitSectionProps;
    content?: AuthSplitContentProps;
  };
};

export function AuthSplitLayout({
  sx,
  cssVars,
  children,
  slotProps,
  layoutQuery = 'md',
}: AuthSplitLayoutProps) {
  const renderHeader = () => {
    const headerSlotProps: HeaderSectionProps['slotProps'] = {
      container: { maxWidth: false },
    };

    const headerSlots: HeaderSectionProps['slots'] = {
      topArea: (
        <Alert severity="info" sx={{ display: 'none', borderRadius: 0 }}>
          This is an info Alert.
        </Alert>
      ),
      leftArea: (
        <>
          {/** @slot Logo */}
          <Logo isLogin />
        </>
      ),
      rightArea: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
          {/** @slot Help link */}

          {/** @slot Settings button */}
        </Box>
      ),
    };

    return (
      <HeaderSection
        disableElevation
        layoutQuery={layoutQuery}
        {...slotProps?.header}
        slots={{ ...headerSlots, ...slotProps?.header?.slots }}
        slotProps={merge(headerSlotProps, slotProps?.header?.slotProps ?? {})}
        sx={[
          { position: { [layoutQuery]: 'fixed' } },
          ...(Array.isArray(slotProps?.header?.sx)
            ? (slotProps?.header?.sx ?? [])
            : [slotProps?.header?.sx]),
        ]}
      />
    );
  };

  const renderFooter = () => (
    <Box
      sx={{
        position: { [layoutQuery]: 'fixed' },
        bottom: 0,
        left: 0,
        width: '100%',
        px: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Typography variant="body1" sx={{ color: '#EDEDEDCC' }} component="span">
        © 2024 Xpanse. All rights reserved.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, p: 2 }}>
        <Link
          href="https://www.xpanse.com/accessibility"
          underline="hover"
          variant="caption"
          color="#EDEDEDCC"
          target="_blank"
          rel="noopener"
        >
          <Typography variant="body1" sx={{ color: '#EDEDEDCC' }} component="span">
            Accessibility
          </Typography>
        </Link>

        <Link
          href="https://www.xpanse.com/privacy-policy"
          underline="hover"
          variant="caption"
          color="#EDEDEDCC"
          target="_blank"
          rel="noopener"
        >
          <Typography variant="body1" sx={{ color: '#EDEDEDCC' }} component="span">
            | Privacy Policy
          </Typography>
        </Link>
        <Link
          href="https://www.xpanse.com/terms-of-service"
          underline="hover"
          variant="caption"
          color="#EDEDEDCC"
          target="_blank"
          rel="noopener"
        >
          <Typography variant="body1" sx={{ color: '#EDEDEDCC' }} component="span">
            | Terms of Service
          </Typography>
        </Link>
      </Box>
    </Box>
  );

  const renderMain = () => (
    <MainSection
      {...slotProps?.main}
      sx={[
        (theme) => ({
          pb: 2,
          [theme.breakpoints.up(layoutQuery)]: { flexDirection: 'row' },
          backgroundImage: `url(${CONFIG.assetsDir}/assets/background/background-login.svg)`,
        }),

        ...(Array.isArray(slotProps?.main?.sx)
          ? (slotProps?.main?.sx ?? [])
          : [slotProps?.main?.sx]),
      ]}
    >
      <AuthSplitSection
        layoutQuery={layoutQuery}
        method={CONFIG.auth.method}
        {...slotProps?.section}
        methods={[
          {
            label: 'Amplify',
            path: paths.auth.amplify.signIn,
            icon: `${CONFIG.assetsDir}/assets/icons/platforms/ic-amplify.svg`,
          },
        ]}
      />
      <AuthSplitContent
        layoutQuery={layoutQuery}
        {...slotProps?.content}
        sx={{ backgroundColor: 'background.default', borderRadius: 2, m: 4 }}
      >
        {children}
      </AuthSplitContent>
    </MainSection>
  );

  return (
    <LayoutSection
      /** **************************************
       * @Header
       *************************************** */
      headerSection={renderHeader()}
      /** **************************************
       * @Footer
       *************************************** */
      footerSection={renderFooter()}
      /** **************************************
       * @Styles
       *************************************** */
      cssVars={{ '--layout-auth-content-width': '420px', ...cssVars }}
      sx={sx}
    >
      {renderMain()}
    </LayoutSection>
  );
}
